import React, { FC, useState, useMemo, useEffect, useContext } from 'react';
import DrawerForms from '@pixel/core/DrawerForms';
import Box from '@pixel/core/Box';
import FormInputLayout from '@pixel/core/FormInputLayout';
import { AutoSelect } from '@pixel/core';
import TextField from '@pixel/core/TextField';
import { useMultipleLocalizedText } from '../../../contexts/Localization/localizationHooks';
import axios from 'axios';
import OdsContext from '../OdsContext';
import { useSnackbar } from '@pixel/core/SnackbarService';
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import { IAddDepartmentPayload, IDepartmentsData } from '../OdsTypes';
import CircularProgress from '@pixel/core/CircularProgress';
import { filterOutCurrentParentBU } from '../OdsTransform';
import { checkForAnyChangesInForm } from "../OdsTransform";

interface AddDepartmentsDrawerProps {
  addDrawerState: boolean;
  setaddDrawerState: any;
  editDepartment: boolean;
  addDepartment: boolean;
  depDrawerData: IAddDepartmentPayload;
  duplicateDepartment: boolean;
  setDuplicateDepartment: any;
  makeDepartmentActive: any;
  makeDepartmentInActive: any;
  deleteDepartment: any;
}

const AddDepartment: FC<AddDepartmentsDrawerProps> = ({
  addDrawerState,
  setaddDrawerState,
  addDepartment,
  depDrawerData,
  editDepartment,
  duplicateDepartment,
  setDuplicateDepartment,
  makeDepartmentActive,
  makeDepartmentInActive,
  deleteDepartment,
}) => {
  const {
    ODS_CANCEL_BUTTON_CAPTION,
    ODS_SAVE_BUTTON_CAPTION,
    ODS_DUPLICATE_CAPTION,
    ODS_MAKE_ACTIVE_CAPTION,
    ODS_MAKE_IN_ACTIVE_CAPTION,
    ODS_DESCRIPTION_CAPTION,
    ODS_LEAVE_FORM_CONFIRMATION_TITLE,
    ODS_LEAVE_FORM_CONFIRMATION_DESC,
    ODS_DEPARTMENT_NAME_REQUIRED,
    ODS_DEPARTMENT_SUBMITTED_SUCCESSFULLY,
    ODS_DEPARTMENT_SUBMIT_FAILED,
    ODS_DEPARTMENT_UDATED_SUCCESSFULLY,
    ODS_DEPARTMENT_UPDATE_FAILED,
    ODS_DEPARTMENT_DUPLICATE_NAME_ERROR,
    ODS_ACTIONS_MAKE_INACTIVE,
    ODS_MAKE_BU_INACTIVATE_SUCCESSFUL,
    ODS_MAKE_BU_ACTIVATE_SUCCESSFUL,
    ODS_DEPARTMENT_SETTINGS,
    ODS_DEPARTMENT_NAME,
    ODS_PARENT_DEPARTMENT,
    ODS_SELECT_PARENT_DEPARTMENT,
    ODS_DELETE,
  } = useMultipleLocalizedText()
  const snack = useSnackbar();
  const confirm = useConfirmation();
  const OrgElementsContext = useContext(OdsContext);

  const addDepartmentPayload: IAddDepartmentPayload = useMemo(() => ({
    parentEId: null,
    name: '',
    description: '',
    createdBy: window._jvselfUser.fullName,
    createdDate: new Date()
  }), []);

  const overlayStyling = {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    alignItems: 'center',
    zIndex: '1',
  }

  const resetDrawerState = () => {
    setaddDrawerState(false);
    setSelectedParentDep(null);
    setFormError({ parentEId: false, name: false });
    setAddDepPayload(addDepartmentPayload);
  }

  const [addDepPayload, setAddDepPayload] = useState<IAddDepartmentPayload>(addDepartmentPayload);
  const [selectedParentDep, setSelectedParentDep] = useState(null);
  const [formError, setFormError] = useState({ parentEId: false, name: false });
  const [parentDepList, setParentDepList] = useState(null);
  const [addDepInProgress, setAddDepInProgress] = useState(false);
  const [showDuplicateErrorName, setShowDuplicateErrorName] = useState(false);

  const [formState, setFormState] = useState<IAddDepartmentPayload>(addDepPayload);
  const columnsToSkip = ['parentName', 'actions', 'active', 'eid', 'companyId', 'subDepartmentsCount', 'createdDate', 'createdBy'];
  const handleAddDepDrawer = () => {
    const onLoadDrawerData = editDepartment || duplicateDepartment ? depDrawerData : formState;
    const isChanged = checkForAnyChangesInForm(addDepPayload, onLoadDrawerData, columnsToSkip);
    if (!isChanged) {
      resetDrawerState();
    } else {
      confirm.open({
        dialogLabelSet: DialogLabelSet.CONFIRM,
        title: ODS_LEAVE_FORM_CONFIRMATION_TITLE,
        description: ODS_LEAVE_FORM_CONFIRMATION_DESC,
        onResolve: (reason: string) => {
          resetDrawerState();
        },
        onReject: (reason: string) => {
          console.log('callback rejected reason: ' + reason);
        }
      });
    }
  };

  const duplicateAddDepAction = () => {
    setDuplicateDepartment(true);
    setAddDepPayload({
      ...addDepPayload,
      parentEId: depDrawerData.parentEId,
      name: '[Copy] '+depDrawerData.name,
      description: depDrawerData.description,
      active: depDrawerData.active,
    });
  };

  const validateForm = () => {
    let temp: any = {};
    temp.name = addDepPayload.name === ''
    setFormError({
      ...temp
    });
    return Object.values(temp).every(x => x == '');
  }

  const onAddDepSaveForm = () => {
    if (validateForm()) {
      setAddDepInProgress(true);
      try {
        if(editDepartment && !duplicateDepartment){
          setAddDepPayload({...addDepPayload, parentEId: depDrawerData.parentEId});
          axios
          .put(window.jvUrls.admin + '/action/ods/departments/' + depDrawerData.eid, addDepPayload, {
            headers: {'Content-Type': 'application/json'}
          })
          .then(function (response) {
            setAddDepInProgress(false);
            if ([201, 200].includes(response.status)) {
              resetDrawerState();
              snack.showSuccess(ODS_DEPARTMENT_UDATED_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
            } else {
              setAddDepInProgress(false);
              snack.showError(ODS_DEPARTMENT_UPDATE_FAILED, OrgElementsContext.snackBarAutoHideTime);
            }
          })
          .catch(function (error) {
            setAddDepInProgress(false);
            snack.showError(error.response.data.errorMessage, OrgElementsContext.snackBarAutoHideTime);
            if(error.response.data.errorMessage.search('name is already present') != -1){
              setFormError({ ...formError, name: true });
              setShowDuplicateErrorName(true)
            }
          });
        } else {
          axios
          .post(window.jvUrls.admin + '/action/ods/departments', addDepPayload, {
            headers: {
              'Content-Type': 'application/json; charset=UTF-8'
            }
          })
          .then(function (response) {
            setAddDepInProgress(false);
            if ([201, 200].includes(response.status)) {
              resetDrawerState();
              snack.showSuccess(ODS_DEPARTMENT_SUBMITTED_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
            } else {
              setAddDepInProgress(false);
              snack.showError(ODS_DEPARTMENT_SUBMIT_FAILED, OrgElementsContext.snackBarAutoHideTime);
            }
          })
          .catch(function (error) {
            setAddDepInProgress(false);
            snack.showError(error.response.data.errorMessage, OrgElementsContext.snackBarAutoHideTime);
            if(error.response.data.errorMessage.search('name is already present') != -1){
              setFormError({ ...formError, name: true });
              setShowDuplicateErrorName(true)
            }
          });
        }
      } catch (error) {
        setAddDepInProgress(false);
        console.log(error);
      }
    }
  }

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setShowDuplicateErrorName(false);
    setAddDepPayload({ ...addDepPayload, [name]: value, parentEId: addDepPayload.parentEId });
    setFormError({ ...formError, name: false });
  }

  const handleAutoSelectChange = (event: any, newValue: any) => {
    let parentEid = null;
    setSelectedParentDep(newValue);
    if (newValue && newValue.name && newValue.eid === '') {
      parentEid = null;
    } else if (newValue == null) {
      parentEid = null;
    } else {
      parentEid = newValue.eid;
    }
    setAddDepPayload({ ...addDepPayload, parentEId: parentEid });
    setFormError({ ...formError, parentEId: false });
  }

  const populateFormForEdit = () => {
    setAddDepPayload({
      ...addDepPayload,
      parentEId: depDrawerData.parentEId,
      name: depDrawerData.name,
      description: depDrawerData.description,
    });
    setSelectedParentDep({ name: depDrawerData.parentName, eid: depDrawerData.parentEId });
    duplicateDepartment && duplicateAddDepAction();
  };

  const fetchparentDepList = async () => {
    try {
      await axios
        .get(window.jvUrls.admin + '/action/ods/departments/parentDepartments')
        .then(function (response) {
          if(editDepartment) {
            setParentDepList(filterOutCurrentParentBU(response.data, depDrawerData.eid));
            populateFormForEdit();
          } else {
            setParentDepList(response.data)
          }
        })
        .catch(function (error) {
          console.log(error);
        });
    } catch (error) {
      console.log(error);
    }
  };

  const handleMakeDepartmentInactive = (depDrawerData: IAddDepartmentPayload) => {
    makeDepartmentInActive(depDrawerData);
    resetDrawerState();
  }

  const handleMakeDepartmentActive = (depDrawerData: IAddDepartmentPayload) => {
    makeDepartmentActive(depDrawerData);
    resetDrawerState();
  }

  const handleDeleteDepartment = (depDrawerData: IAddDepartmentPayload) => {
    deleteDepartment(depDrawerData);
    resetDrawerState();
  }

  const populateActionsList = (actionList: any) => {
    const DepActions = [
      { i18Label: ODS_DUPLICATE_CAPTION, name: "duplicate" },
      { i18Label: ODS_MAKE_ACTIVE_CAPTION, name: "makeActive" },
      { i18Label: ODS_ACTIONS_MAKE_INACTIVE, name: "makeInactive" },
      { i18Label: ODS_DELETE, name: "delete" },
    ];
    const actionLinks: any = [];
    if (actionList) {
      Object.keys(actionList).forEach((key) => {
        let filteredAction = DepActions.filter((e) => e.name === key);
        if (filteredAction.length > 0 && actionList[key] !== 'hidden' && actionList[key] !== null) {
          let actionTertiaryLink = {
            type: "tertiary",
            label: filteredAction[0].i18Label,
            disabled: actionList[key] === 'grayout',
            onClick: () => {
              filteredAction[0].name === 'duplicate' && duplicateAddDepAction();
              filteredAction[0].name === 'makeActive' && handleMakeDepartmentActive(depDrawerData);
              filteredAction[0].name === 'makeInactive' && handleMakeDepartmentInactive(depDrawerData);
              filteredAction[0].name === 'delete' && handleDeleteDepartment(depDrawerData);
            }
          };
          actionLinks.push(actionTertiaryLink);
        }
      });
    }
    return actionLinks;
  }

  useEffect(() => {
    if (editDepartment) {
      setAddDepPayload({ ...addDepPayload, name: depDrawerData.name, description: depDrawerData.description, parentEId: depDrawerData.parentEId });
      depDrawerData.parentEId && setSelectedParentDep({ name: depDrawerData.parentName, eid: depDrawerData.parentEId });
      setFormState({ ...addDepPayload, name: depDrawerData.name, description: depDrawerData.description, parentEId: depDrawerData.parentEId });
    } else if(addDepartment && depDrawerData){
      setAddDepPayload({ ...addDepPayload, parentEId: depDrawerData.eid });
      setSelectedParentDep({ name: depDrawerData.name, eid: depDrawerData.eid });
      setFormState({ ...addDepPayload, parentEId: depDrawerData.eid });
    }
  }, [parentDepList]);

  useEffect(() => {
    addDrawerState && fetchparentDepList();
  }, [addDrawerState]);

  const DrawerPartionStyle = {
    '& .drawer-container-left-section': {
      '& > .MuiBox-root': {
        marginTop: 2,
      },
      overflow: 'auto',
      paddingRight: '24px',
      marginRight: '24px',
    },
  };

  return (
    <div>
      <DrawerForms
        labelDrawerForm={ODS_DEPARTMENT_SETTINGS}
        labelSaveDrawerForm={editDepartment && !duplicateDepartment ? 'Update' : ODS_SAVE_BUTTON_CAPTION}
        labelClose={ODS_CANCEL_BUTTON_CAPTION}
        anchor='right'
        drawerWidth='30%'
        onSaveForm={onAddDepSaveForm}
        onCancel={handleAddDepDrawer}
        open={addDrawerState}
        SelectedActionGroupProps={!addDepartment && depDrawerData && !duplicateDepartment && {
          id: 'drawer-from-actions',
          variant: 'standard_more',
          disableIconButton: false,
          actionLabel: 'Custom drawer actions',
          actions: populateActionsList(depDrawerData.actions),
        }}
      >
        {(addDepInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
        <Box sx={DrawerPartionStyle} className={'admin-ODS-drawer-container'}>
        <FormInputLayout gridLayout='calc(50% - 6px)'>
          <Box className={'dep-name-text-field'}><TextField label={ODS_DEPARTMENT_NAME} maxLength={200} error={formError.name} errorText={showDuplicateErrorName ? ODS_DEPARTMENT_DUPLICATE_NAME_ERROR : ODS_DEPARTMENT_NAME_REQUIRED} name='name' onChange={handleInputChange} value={addDepPayload.name} fullWidth required></TextField></Box>
          <Box className={'dep-parent-field'}>
          <AutoSelect
                id="parentDepartment"
                fullWidth
                label={ODS_PARENT_DEPARTMENT}
                name='parentEId'
                value={selectedParentDep}
                options={parentDepList}
                getOptionLabel={(option: any) => option.name}
                onChange={handleAutoSelectChange}
                isOptionEqualToValue={(option: any, value: any) => option.eid === value.eid}
                placeholder={ODS_SELECT_PARENT_DEPARTMENT}
              />
          </Box>
        </FormInputLayout>
        <Box className={'dep-description-field'}><TextField onChange={handleInputChange} name='description' value={addDepPayload.description} label={ODS_DESCRIPTION_CAPTION} multiline rows={6} fullWidth ></TextField></Box>
        </Box>
      </DrawerForms>
    </div>
  );
};

export default AddDepartment;